import React, { useState, useEffect, useRef } from "react";
import { Box, TextField, IconButton, Paper, Typography, CircularProgress, Tooltip } from "@mui/material";
import { Send, Close } from "@mui/icons-material";
import Draggable from "react-draggable";
import { sendQueryToSQL } from "./action.ts";
import "./query-widget.scss";

interface ChatMessage {
  id: string;
  type: 'user' | 'ai';
  content: string;
  timestamp: number;
}

const QueryWidget = () => {
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [chatHistory, setChatHistory] = useState<ChatMessage[]>([]);
  const [inputError, setInputError] = useState("");
  const [previousContext, setPreviousContext] = useState<{ query: string | null; result: any | null }>({ 
    query: null, 
    result: null 
  });

  const [chatWidth, setChatWidth] = useState(400);
  const [chatHeight, setChatHeight] = useState(500);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeDirection, setResizeDirection] = useState<string | null>(null);
  
  const widgetRef = useRef<HTMLDivElement>(null);
  const bubbleRef = useRef<HTMLDivElement>(null);
  const chatHistoryRef = useRef<HTMLDivElement>(null);
  
  const isMobile = window.innerWidth <= 600;
  const [touchStart, setTouchStart] = useState({ x: 0, y: 0 });
  
  const userDetailsFromStorage = JSON.parse(localStorage.getItem("userdetails") || "null");
  const userRole = userDetailsFromStorage?.role || '';
  const companyId = userDetailsFromStorage?.companies_id || '';

  useEffect(() => {
    const savedChatHistory = localStorage.getItem(`chatHistory_${companyId}`);
    const chatTimestamp = localStorage.getItem(`chatTimestamp_${companyId}`);
    
    if (savedChatHistory && chatTimestamp) {
      const savedTime = parseInt(chatTimestamp);
      const currentTime = Date.now();
      const oneDayInMs = 24 * 60 * 60 * 1000;
      
      if (currentTime - savedTime > oneDayInMs) {
        localStorage.removeItem(`chatHistory_${companyId}`);
        localStorage.removeItem(`chatTimestamp_${companyId}`);
        setChatHistory([]);
      } else {
        try {
          const parsedChat = JSON.parse(savedChatHistory);
          setChatHistory(parsedChat);
        } catch (e) {
          setChatHistory([]);
        }
      }
    }
  }, [companyId]);

  useEffect(() => {
    if (chatHistory.length > 0 && companyId) {
      localStorage.setItem(`chatHistory_${companyId}`, JSON.stringify(chatHistory));
      if (!localStorage.getItem(`chatTimestamp_${companyId}`)) {
        localStorage.setItem(`chatTimestamp_${companyId}`, Date.now().toString());
      }
    }
  }, [chatHistory, companyId]);

  useEffect(() => {
    if (showChat && chatHistoryRef.current) {
      setTimeout(() => {
        if (chatHistoryRef.current) {
          chatHistoryRef.current.scrollTop = chatHistoryRef.current.scrollHeight;
        }
      }, 0);
    }
  }, [chatHistory, showChat]);

  useEffect(() => {
    const handleStorageChange = () => {
      if (!localStorage.getItem("userdetails")) {
        setChatHistory([]);
        const keys = Object.keys(localStorage);
        keys.forEach(key => {
          if (key.startsWith('chatHistory_') || key.startsWith('chatTimestamp_')) {
            localStorage.removeItem(key);
          }
        });
      }
    };
    window.addEventListener('beforeunload', handleStorageChange);
    return () => window.removeEventListener('beforeunload', handleStorageChange);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!showChat && bubbleRef.current?.contains(event.target as Node)) {
        return;
      }
      if (
        showChat &&
        widgetRef.current &&
        !widgetRef.current.contains(event.target as Node) &&
        !bubbleRef.current?.contains(event.target as Node)
      ) {
        handleCloseChat();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);   
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showChat]);

  useEffect(() => {
    const handleTouchOutside = (event: TouchEvent) => {
      if (!showChat && bubbleRef.current?.contains(event.target as Node)) {
        return;
      }
      
      if (
        showChat &&
        widgetRef.current &&
        !widgetRef.current.contains(event.target as Node) &&
        !bubbleRef.current?.contains(event.target as Node)
      ) {
        handleCloseChat();
      }
    };

    document.addEventListener("touchstart", handleTouchOutside);
    
    return () => {
      document.removeEventListener("touchstart", handleTouchOutside);
    };
  }, [showChat]);

  const handleCloseChat = () => {
    setShowChat(false);
    setQuery("");
    setInputError(""); // Clear error on close
    if (companyId) {
      localStorage.removeItem(`pagination_${companyId}`);
    }
  };

  const handleClearHistory = () => {
    setChatHistory([]);
    setPreviousContext({ query: null, result: null }); 
    if (companyId) {
      localStorage.removeItem(`chatHistory_${companyId}`);
      localStorage.removeItem(`chatTimestamp_${companyId}`);
    }
  };

  const handleResizeStart = (e: React.MouseEvent, direction: string) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    setResizeDirection(direction);
  };

  useEffect(() => {
    if (!isResizing || !resizeDirection) return;

    const handleMouseMove = (e: MouseEvent) => {
      e.preventDefault();
      if (widgetRef.current) {
        const rect = widgetRef.current.getBoundingClientRect();
        
        if (resizeDirection.includes('right')) {
          const newWidth = e.clientX - rect.left;
          if (newWidth > 300 && newWidth < 1200) setChatWidth(newWidth);
        }
        
        if (resizeDirection.includes('left')) {
          const newWidth = rect.right - e.clientX;
          if (newWidth > 300 && newWidth < 1200) setChatWidth(newWidth);
        }
        
        if (resizeDirection.includes('bottom')) {
          const newHeight = e.clientY - rect.top;
          if (newHeight > 200 && newHeight < 800) setChatHeight(newHeight);
        }
        
        if (resizeDirection.includes('top')) {
          const newHeight = rect.bottom - e.clientY;
          if (newHeight > 200 && newHeight < 800) setChatHeight(newHeight);
        }
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      setResizeDirection(null);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, resizeDirection]);

  if (userRole === "platform_user") {
    return null;
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    setQuery(rawValue); 

    const forbiddenRegex = /['";\\%<>{}*]|--/;
    
    if (forbiddenRegex.test(rawValue)) {
      setInputError("Invalid Character");
    } else {
      setInputError(""); 
    }
  };

  const handleSend = () => {
    if (!query.trim() || !companyId || inputError) return;

    const currentQuery = query.trim();
    const lowerQuery = currentQuery.toLowerCase();

    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      type: 'user',
      content: currentQuery,
      timestamp: Date.now()
    };
    setChatHistory(prev => [...prev, userMessage]);

    setQuery("");
    setInputError(""); 
    setLoading(true);

    const storageKey = `pagination_${companyId}`;
    
    let payload: any = {
      companies_id: companyId,
      user_id: userDetailsFromStorage?.id,
      previous_query: previousContext.query,
      previous_result: previousContext.result
    };
    
    if (lowerQuery === "yes") {
      const lastSession = localStorage.getItem(storageKey);

      if (lastSession) {
        const sessionData = JSON.parse(lastSession) as {
          sql: string;
          offset?: number;
        };

        payload = {
          ...payload,
          query: "yes",
          sql: sessionData.sql,
          offset: sessionData.offset ?? 0,  
        };
      } else {
        const errorMsg = "No previous query to continue. Please enter a new query.";
        const aiMessage: ChatMessage = {
          id: `ai-${Date.now()}`,
          type: 'ai',
          content: errorMsg,
          timestamp: Date.now()
        };
        setChatHistory(prev => [...prev, aiMessage]);
        setLoading(false);
        return;
      }
    } else {
      payload.query = currentQuery;
      payload.offset = 0;
      localStorage.removeItem(storageKey);
    }

    sendQueryToSQL(
      payload,
      (res: any) => {
        const aiMessage: ChatMessage = {
          id: `ai-${Date.now()}`,
          type: 'ai',
          content: res.natural_response || "No response from AI",
          timestamp: Date.now()
        };
        setChatHistory(prev => [...prev, aiMessage]);
        setLoading(false);
        
        if (res.result) {
          setPreviousContext({
            query: currentQuery,
            result: res.result
          });
        }

        if (
          res.result &&
          res.result.length > 0 &&
          res.type !== "tracking_id" &&
          res.type !== "order_id"
        ) {
          const newOffset = res.next_offset ?? 0;

          localStorage.setItem(
            storageKey,
            JSON.stringify({
              sql: res.sql,
              offset: newOffset,
            })
          );
        } else {
          localStorage.removeItem(storageKey);
        }
      },
      (errMsg: any) => {
        const aiMessage: ChatMessage = {
          id: `ai-${Date.now()}`,
          type: 'ai',
          content: errMsg,
          timestamp: Date.now()
        };
        setChatHistory(prev => [...prev, aiMessage]);
        setLoading(false);
      }
    );
  };

  const onTouchStart = (e: any) => {
    setTouchStart({
      x: e.touches[0].clientX,
      y: e.touches[0].clientY
    });
  };

  const onTouchEndBubble = (e: any) => {
    const dx = Math.abs(e.changedTouches[0].clientX - touchStart.x);
    const dy = Math.abs(e.changedTouches[0].clientY - touchStart.y);

    if (dx < 5 && dy < 5) {
      setShowChat(true);
    }
  };

  return (
    <>
      {!showChat ? (
        <Draggable handle=".draggable-bubble">
          <Box
            ref={bubbleRef}
            className="draggable-bubble"
            sx={{
              position: "fixed",
              bottom: isMobile ? '25%' : 0, // Moves the widget up by 25% of the viewport height on mobile
              right: 0,                     // Anchors the widget strictly to the right side
              cursor: "pointer",
              zIndex: 1000,
            }}
          >
            <Tooltip
              title="Search for vehicle and trip details using a number plate or trip ID. AI will provide a clear response."
              arrow
              placement="left"
              componentsProps={{ tooltip: { sx: { maxWidth: 140 } } }}
              PopperProps={{
                modifiers: [{ name: "offset", options: { offset: [0, -40] } }],
              }}
            >
              <img
                src="/robot.png"
                alt="AI Assistant"
                style={{ width: "140px", height: "140px" }}
                onTouchStart={onTouchStart}
                onTouchEnd={onTouchEndBubble}
                onClick={() => setShowChat(true)}
              />
            </Tooltip>
          </Box>
        </Draggable>
      ) : (
        <Draggable handle=".drag-handle" cancel=".no-drag">
          <Box
            ref={widgetRef}
            className="query-draggable-wrapper drag-handle"
            sx={{
              padding: "1px",
              zIndex: 1001,
              position: "fixed",
              bottom: 20,
              right: 20,
              width: isMobile ? "90vw" : `${chatWidth}px`,
              height: isMobile ? "75vh" : `${chatHeight}px`,
              maxHeight: "85vh",
              maxWidth: "90vw",
              cursor: isResizing ? 'nwse-resize' : 'auto',
              userSelect: isResizing ? 'none' : 'auto',
              overflow: 'hidden',
            }}
          >
            <Box className="query-widget" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Paper elevation={5} className="query-box" sx={{ p: 2, position: 'relative', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <Box 
                  display="flex" 
                  justifyContent="space-between" 
                  alignItems="center" 
                  mb={2}
                  className="drag-handle"
                  sx={{ cursor: 'move' }}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <img
                      src="/robot-icon.webp"
                      alt="Robot Icon"
                      style={{ height: "32px", width: "50px", objectFit: "contain" }}
                    />
                    <Typography variant="subtitle1" fontWeight="bold">
                      AI Assistant
                    </Typography>
                  </Box>
                  <Box display="flex" gap={0.5}>
                    <IconButton
                      size="small"
                      onClick={handleCloseChat}
                      className="no-drag"
                      sx={{ p: 0.5, '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' } }}
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>

                <Box
                  ref={chatHistoryRef}
                  className="no-drag"
                  sx={{
                    flex: 1,
                    minHeight: '100px',
                    overflowY: 'auto',
                    mb: 2,
                    p: 2,
                    backgroundColor: 'grey.50',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'grey.200',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.5,
                  }}
                >
                  {chatHistory.length === 0 ? (
                    <Typography
                      variant="caption"
                      sx={{ color: 'text.secondary', textAlign: 'center', my: 'auto' }}
                    >
                      No messages yet. Start a conversation!
                    </Typography>
                  ) : (
                    chatHistory.map((msg) => (
                      <Box
                        key={msg.id}
                        sx={{
                          display: 'flex',
                          justifyContent: msg.type === 'user' ? 'flex-end' : 'flex-start',
                        }}
                      >
                        <Box
                          sx={{
                            maxWidth: '85%',
                            p: 1.5,
                            borderRadius: 1.5,
                            backgroundColor: msg.type === 'user' ? 'primary.main' : 'grey.200',
                            color: msg.type === 'user' ? 'white' : 'text.primary',
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              lineHeight: 1.5,
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                            }}
                            dangerouslySetInnerHTML={{ __html: msg.content }}
                          />
                        </Box>
                      </Box>
                    ))
                  )}
                  {loading && (
                    <Box display="flex" alignItems="center" gap={1}>
                      <CircularProgress size={16} />
                      <Typography variant="caption" color="textSecondary">
                        AI is thinking...
                      </Typography>
                    </Box>
                  )}
                </Box>

                <Box display="flex" alignItems="flex-start" gap={1} mb={2}>
                  <TextField
                    className="no-drag"
                    fullWidth
                    size="small"
                    placeholder="Type your query..."
                    value={query}
                    onChange={handleInputChange}
                    error={!!inputError}
                    helperText={inputError}
                    multiline
                    minRows={1}
                    maxRows={4}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        if (!inputError) handleSend(); 
                      }
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '20px',
                      }
                    }}
                  />
                  <IconButton
                    className="no-drag"
                    color="primary"
                    onClick={handleSend}
                    disabled={loading || !companyId || !query.trim() || !!inputError}
                    sx={{
                      mt: inputError ? 0 : 0, 
                      backgroundColor: 'primary.main',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'primary.dark',
                      },
                      '&.Mui-disabled': {
                        backgroundColor: 'action.disabledBackground',
                        color: 'action.disabled',
                      }
                    }}
                  >
                    <Send />
                  </IconButton>
                </Box>

                {!isMobile && (
                  <>
                    <Box onMouseDown={(e) => handleResizeStart(e, 'top')} sx={{ position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)', width: '60%', height: '6px', cursor: 'ns-resize', backgroundColor: isResizing ? 'rgba(25, 118, 210, 0.4)' : 'rgba(0, 0, 0, 0.05)', '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.25)' }, zIndex: 1000, pointerEvents: 'auto' }} />
                    <Box onMouseDown={(e) => handleResizeStart(e, 'bottom')} sx={{ position: 'absolute', bottom: 0, left: '50%', transform: 'translateX(-50%)', width: '60%', height: '6px', cursor: 'ns-resize', backgroundColor: isResizing ? 'rgba(25, 118, 210, 0.4)' : 'rgba(0, 0, 0, 0.05)', '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.25)' }, zIndex: 1000, pointerEvents: 'auto' }} />
                    <Box onMouseDown={(e) => handleResizeStart(e, 'left')} sx={{ position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)', width: '6px', height: '60%', cursor: 'ew-resize', backgroundColor: isResizing ? 'rgba(25, 118, 210, 0.4)' : 'rgba(0, 0, 0, 0.05)', '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.25)' }, zIndex: 1000, pointerEvents: 'auto' }} />
                    <Box onMouseDown={(e) => handleResizeStart(e, 'right')} sx={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)', width: '6px', height: '60%', cursor: 'ew-resize', backgroundColor: isResizing ? 'rgba(25, 118, 210, 0.4)' : 'rgba(0, 0, 0, 0.05)', '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.25)' }, zIndex: 1000, pointerEvents: 'auto' }} />
                    <Box onMouseDown={(e) => handleResizeStart(e, 'top-left')} sx={{ position: 'absolute', top: 0, left: 0, width: '12px', height: '12px', cursor: 'nwse-resize', backgroundColor: isResizing ? 'rgba(25, 118, 210, 0.6)' : 'transparent', zIndex: 1001, pointerEvents: 'auto' }} />
                    <Box onMouseDown={(e) => handleResizeStart(e, 'top-right')} sx={{ position: 'absolute', top: 0, right: 0, width: '12px', height: '12px', cursor: 'nesw-resize', backgroundColor: isResizing ? 'rgba(25, 118, 210, 0.6)' : 'transparent', zIndex: 1001, pointerEvents: 'auto' }} />
                    <Box onMouseDown={(e) => handleResizeStart(e, 'bottom-left')} sx={{ position: 'absolute', bottom: 0, left: 0, width: '12px', height: '12px', cursor: 'nesw-resize', backgroundColor: isResizing ? 'rgba(25, 118, 210, 0.6)' : 'transparent', zIndex: 1001, pointerEvents: 'auto' }} />
                    <Box onMouseDown={(e) => handleResizeStart(e, 'bottom-right')} sx={{ position: 'absolute', bottom: 0, right: 0, width: '12px', height: '12px', cursor: 'nwse-resize', backgroundColor: isResizing ? 'rgba(25, 118, 210, 0.6)' : 'transparent', zIndex: 1001, pointerEvents: 'auto' }} />
                  </>
                )}
              </Paper>
            </Box>
          </Box>
        </Draggable>
      )}
    </>
  );
};

export default QueryWidget;